import React from 'react';
import {Table, Tag, Typography} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {useTranslation} from 'react-i18next';
import {PodExemptSpotTaskForDspType} from '../../graphql/podExempt/types';

interface Props {
  data: PodExemptSpotTaskForDspType[];
  loading: boolean;
}

/**
 * DSP「抽查记录」列表（数据来自监控侧抽查系统，本侧只做展示）。
 *
 * 空值语义（监控侧契约）：任务未完成时 conclusion / assigneeName / doneAt / sampleCount 正常为空，
 * 不是异常，一律回退「-」；targetAddressCount 单地址维度恒为空，围栏未完成物化时也为空。
 */
const PodExemptSpotCheckTable: React.FC<Props> = ({data, loading}) => {
  const {t} = useTranslation();

  /** 抽查结论是枚举，需映射文案；驳回理由是 QA 自由文本，原样展示不做映射 */
  const renderConclusion = (conclusion: string | null) => {
    if (!conclusion) {
      return <Tag>{t('podExempt.spotCheck.pending')}</Tag>;
    }
    return conclusion === 'MAINTAIN' ? (
      <Tag color="green">{t('podExempt.spotCheck.conclusionMaintain')}</Tag>
    ) : (
      <Tag color="red">{t('podExempt.spotCheck.conclusionOffline')}</Tag>
    );
  };

  const columns: ColumnsType<PodExemptSpotTaskForDspType> = [
    {
      title: t('podExempt.spotCheck.taskNo'),
      dataIndex: 'taskNo',
      width: 110,
      render: (value: string | null, record) => value ?? `SP-${record.id}`,
    },
    {
      title: t('podExempt.spotCheck.targetNo'),
      dataIndex: 'targetNo',
      width: 120,
      render: (value: string | null) => value ?? '-',
    },
    {
      title: t('podExempt.spotCheck.targetAddress'),
      dataIndex: 'targetAddress',
      width: 260,
      render: (value: string | null, record) => {
        // 副行：邮编 +（围栏维度）地址数，与「已通过的地址」页同款两行式
        const metaParts = [record.targetZipcode].filter(Boolean) as string[];
        if (record.targetType === 'FENCE') {
          metaParts.push(
            record.targetAddressCount != null
              ? t('podExempt.field.addressCountUnit', {count: record.targetAddressCount})
              : t('podExempt.field.addressCounting'),
          );
        }
        return (
          <div className="leading-normal">
            <div className="break-words">{value ?? '-'}</div>
            {metaParts.length > 0 && (
              <div className="text-xs text-gray-400 mt-0.5">{metaParts.join(' · ')}</div>
            )}
          </div>
        );
      },
    },
    {
      title: t('podExempt.spotCheck.batch'),
      dataIndex: 'periodNo',
      width: 150,
      render: (value: string | null, record) => {
        if (!value) {
          return '-';
        }
        const typeKey = record.periodType ? `podExempt.spotCheck.period${record.periodType}` : null;
        const typeLabel = typeKey ? (t(typeKey, {defaultValue: ''}) as string) : '';
        return typeLabel ? `${value} ${typeLabel}` : value;
      },
    },
    {
      title: t('podExempt.spotCheck.sample'),
      dataIndex: 'sampleCount',
      width: 150,
      render: (value: number | null, record) =>
        // 未抽样时两个数都为空 → 整列「-」，不显示「抽 0 单 / 不通过 0 单」造成误读
        value == null
          ? '-'
          : t('podExempt.spotCheck.sampleValue', {sample: value, fail: record.failCount ?? 0}),
    },
    {
      title: t('podExempt.spotCheck.assignee'),
      dataIndex: 'assigneeName',
      width: 130,
      render: (value: string | null) => value ?? '-',
    },
    {
      title: t('podExempt.spotCheck.doneAt'),
      dataIndex: 'doneAt',
      width: 160,
      render: (value: string | null) => (value ? new Date(value).toLocaleString() : '-'),
    },
    {
      title: t('podExempt.spotCheck.conclusion'),
      dataIndex: 'conclusion',
      width: 110,
      render: (value: string | null) => renderConclusion(value),
    },
    {
      title: t('podExempt.spotCheck.remark'),
      dataIndex: 'conclusionRemark',
      width: 260,
      render: (value: string | null) => (
        <Typography.Text className="whitespace-pre-wrap break-words">{value ?? '-'}</Typography.Text>
      ),
    },
  ];

  return (
    <Table
      rowKey="id"
      size="small"
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={false}
      scroll={{x: 'max-content'}}
    />
  );
};

export default PodExemptSpotCheckTable;
